import React, { useState, useEffect } from 'react'
import api from '../services/api'

interface LeadFollowUpProps {
  leadId: number
  leadName: string
  followUpDate: string | null
  onFollowUpUpdated?: (date: string) => void
}

const LeadFollowUp: React.FC<LeadFollowUpProps> = ({ 
  leadId, 
  leadName, 
  followUpDate,
  onFollowUpUpdated 
}) => {
  const [currentDate, setCurrentDate] = useState(followUpDate || '')
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isOverdue = (dateString: string) => {
    if (!dateString) return false
    return new Date(dateString) < new Date()
  }

  const handleSaveFollowUp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentDate) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      await api.patch(`/leads/${leadId}/`, { follow_up_date: currentDate })
      setIsEditing(false)
      if (onFollowUpUpdated) {
        onFollowUpUpdated(currentDate)
      }
      alert('Follow-up agendado com sucesso!')
    } catch (err: any) {
      console.error('Error saving follow-up:', err)
      alert('Erro ao agendar follow-up: ' + (err.message || 'Erro desconhecido'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleClearFollowUp = async () => {
    if (!window.confirm('Tem certeza que deseja remover o follow-up?')) {
      return
    }

    try {
      setIsSaving(true)
      await api.patch(`/leads/${leadId}/`, { follow_up_date: null })
      setCurrentDate('')
      if (onFollowUpUpdated) {
        onFollowUpUpdated('')
      }
      alert('Follow-up removido com sucesso!')
    } catch (err: any) {
      console.error('Error clearing follow-up:', err)
      alert('Erro ao remover follow-up: ' + (err.message || 'Erro desconhecido'))
    } finally {
      setIsSaving(false)
    }
  }

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
    
    if (days < 0) return Math.abs(days)
    return days
  }

  return (
    <div style={{ padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{ fontSize: '32px' }}>⏰</div>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0' }}>
            Próximo Follow-up
          </h4>
          {currentDate ? (
            <div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {formatDate(currentDate)}
              </div>
              <div style={{ marginTop: '4px' }}>
                {isOverdue(currentDate) ? (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: '#ffebee',
                    color: '#c62828'
                  }}>
                    ⚠️ Vencido há {getDaysUntil(currentDate)} dias
                  </span>
                ) : (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32'
                  }}>
                    ✓ Em {getDaysUntil(currentDate)} dias
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#999' }}>
              Nenhum follow-up agendado
            </div>
          )}
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSaveFollowUp}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>
              Data e Hora do Follow-up
            </label>
            <input
              type="datetime-local"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="submit"
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isSaving ? 0.5 : 1
              }}
            >
              {isSaving ? 'Salvando...' : '✓ Agendar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false)
                setCurrentDate(followUpDate || '')
              }}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              ✕ Cancelar
            </button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setIsEditing(true)}
            style={{
              flex: 1,
              padding: '8px 16px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📅 {currentDate ? 'Alterar' : 'Agendar'}
          </button>
          {currentDate && (
            <button
              onClick={handleClearFollowUp}
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isSaving ? 0.5 : 1
              }}
            >
              🗑️ Remover
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default LeadFollowUp
